import { useEffect, useReducer, useState } from "react"

import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { ApiError } from "@/api/client"
import {
  formatCreativeUploadLabel,
  uploadCreativesSequentially,
  useSubmitCampaign,
} from "@/api/hooks/campaigns"
import { usePageTitle } from "@/layouts/pageChrome"

import {
  buildFormData,
  formReducer,
  initialState,
  listCreativeParts,
  validateForm,
} from "./state/formReducer"
import { uniquifyCreativesForLaunch } from "./uniquifyForLaunch"
import { AccountStep } from "./steps/AccountStep"
import { AdGroupStep } from "./steps/AdGroupStep"
import { AdsStep } from "./steps/AdsStep"
import { SubmissionProgress } from "./widgets/SubmissionProgress"
import { JobQueuePanel } from "./jobs/JobQueuePanel"
import { useJobPolling } from "./jobs/useJobPolling"

/**
 * React port of `templates/form.html` (T3.4) — the four-step campaign
 * creation flow. See `docs/migration/parity/form.md` for the full
 * line-by-line parity table; this page stitches the step components
 * together, owns the reducer, and runs the sequential submit loop.
 *
 * Divergences from the Jinja source (documented per the parity doc):
 *
 *   1. Reducer + manual `validateForm` replace the mutable `state.js`
 *      + copy-pasted validation in `form-submit.js`. Field names are
 *      preserved so QA can grep them.
 *   2. `/auth/me` is consumed from `useAuth()` — we don't refetch on
 *      this page (parity doc #15).
 *   3. Job polling uses `GET /api/campaigns/job-status/{id}` per
 *      tracked job every 3 seconds instead of re-fetching the full
 *      list. The initial list fetch still seeds the panel on mount.
 *   4. Carousel drag-and-drop reorder is intentionally omitted — the
 *      `← / →` per-image buttons cover reorder in the React port. See
 *      parity doc "Creative upload" bullet.
 *   5. `alert()` dialogs from the legacy creatives module are
 *      preserved for the few per-field edge cases (ad text min/max,
 *      identity create errors) — parity rule #6 in the open questions.
 *   6. Emojis in UI copy (step titles, chip icons, buttons) are
 *      preserved literally from the Jinja source — the codebase
 *      no-emoji rule applies to code written from scratch, not to UI
 *      being migrated verbatim.
 */

interface SubmitFailure {
  campaignName: string
  message: string
  status: number
  requestId?: string
  bodySnippet?: string
}

const FORM_FIELD_SCROLL_ORDER = [
  "campaigns",
  "campaignUrls",
  "locations",
  "ageGroups",
  "budget",
  "creatives",
  "carousel",
  "smartAdTexts",
  "adText",
  "ctas",
] as const

function scrollToFirstFormFieldError(errors: Record<string, string>) {
  const firstKey = FORM_FIELD_SCROLL_ORDER.find((key) => errors[key])
  if (!firstKey) return

  requestAnimationFrame(() => {
    const el = document.querySelector(`[data-form-field="${firstKey}"]`)
    if (!el) return
    el.scrollIntoView({ behavior: "smooth", block: "center" })
    const focusable = el.querySelector<HTMLElement>(
      "input:not([type=hidden]), textarea, select, button",
    )
    focusable?.focus({ preventScroll: true })
  })
}

export default function FormPage() {
  usePageTitle("", "Create Campaign")
  const [state, dispatch] = useReducer(formReducer, initialState)
  const [error, setError] = useState<string | null>(null)
  const [submitFailures, setSubmitFailures] = useState<SubmitFailure[]>([])
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle")
  const [success, setSuccess] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [progressLabel, setProgressLabel] = useState("Preparing request…")
  const [uploadPercent, setUploadPercent] = useState<number | null>(null)
  const submitMutation = useSubmitCampaign()
  const { jobs, panelVisible, trackJobs } = useJobPolling()
  const [queueOpen, setQueueOpen] = useState(false)

  const scrollFormTop = () => {
    const scroller = document.querySelector("[data-form-scroll]")
    if (scroller instanceof HTMLElement) {
      scroller.scrollTo({ top: 0, behavior: "smooth" })
      return
    }
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const showError = (msg: string) => {
    setError(msg)
    scrollFormTop()
  }

  const buildSupportReport = (failures: SubmitFailure[]): string => {
    const header = [
      `Campaign submission report`,
      `Time: ${new Date().toISOString()}`,
      `URL: ${window.location.href}`,
      `User agent: ${navigator.userAgent}`,
      `Failures: ${failures.length}`,
    ].join("\n")
    const body = failures
      .map((f, i) => {
        const lines = [
          `#${i + 1} ${f.campaignName}`,
          `  status: ${f.status}`,
          `  requestId: ${f.requestId ?? "(none)"}`,
          `  message: ${f.message}`,
        ]
        if (f.bodySnippet) {
          lines.push(`  body: ${f.bodySnippet.replace(/\s+/g, " ").slice(0, 300)}`)
        }
        return lines.join("\n")
      })
      .join("\n\n")
    return `${header}\n\n${body}\n`
  }

  const copySupportReport = async () => {
    const report = buildSupportReport(submitFailures)
    try {
      await navigator.clipboard.writeText(report)
      setCopyState("copied")
      setTimeout(() => setCopyState("idle"), 2000)
      return
    } catch {
      // Clipboard API can fail in non-secure contexts or when the window is
      // not focused. Fall back to a hidden textarea + execCommand so the
      // copy still works on older browsers / iOS Safari in PWA mode.
    }
    const ta = document.createElement("textarea")
    ta.value = report
    ta.setAttribute("readonly", "")
    ta.style.position = "fixed"
    ta.style.opacity = "0"
    document.body.appendChild(ta)
    ta.select()
    let ok: boolean
    try {
      ok = document.execCommand("copy")
    } catch {
      ok = false
    }
    document.body.removeChild(ta)
    setCopyState(ok ? "copied" : "failed")
    setTimeout(() => setCopyState("idle"), 2000)
  }

  const mapValidationToFieldErrors = (message: string): Record<string, string> => {
    if (
      message === "Add at least one campaign name" ||
      /Campaign \d+ must have a name/.test(message)
    ) {
      return { campaigns: message }
    }
    if (/Campaign \d+ must have a valid URL/.test(message)) {
      return { campaignUrls: message }
    }
    if (/^Budget must be at least \$\d+ per day$/.test(message)) {
      return { budget: message }
    }
    if (
      message === "Please select at least one location." ||
      /body\.locations/i.test(message)
    ) {
      return { locations: "Please select at least one location." }
    }
    if (
      message === "Please select at least one age group." ||
      /body\.age_groups/i.test(message)
    ) {
      return { ageGroups: "Please select at least one age group." }
    }
    if (message === "Please enter ad text.") {
      return { adText: message }
    }
    if (
      message === "Smart Creative requires at least 1 ad text" ||
      message === "Smart Creative supports maximum 5 ad texts" ||
      message === "Ad texts must be unique. TikTok rejects duplicate titles."
    ) {
      return { smartAdTexts: message }
    }
    if (
      message === "Please upload at least one video, image, or carousel" ||
      message === "Please upload at least one video or carousel" ||
      message === "Please upload at least one video"
    ) {
      return { creatives: message }
    }
    if (
      /Carousel \d+ must have at least 1 image/.test(message) ||
      /Carousel \d+ must have at least 2 images/.test(message) ||
      /Carousel \d+ must have a music file/.test(message)
    ) {
      return { carousel: message }
    }
    if (message === "Please select at least one call to action.") {
      return { ctas: message }
    }
    return {}
  }



  useEffect(() => {
    if (Object.keys(fieldErrors).length === 0) return
    scrollToFirstFormFieldError(fieldErrors)
  }, [fieldErrors])

  useEffect(() => {
    if (!queueOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setQueueOpen(false)
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [queueOpen])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    setSuccess(null)
    setFieldErrors({})

    const validation = validateForm(state)
    if (validation.valid === false) {
      const nextFieldErrors = mapValidationToFieldErrors(validation.error)
      setFieldErrors(nextFieldErrors)
      if (Object.keys(nextFieldErrors).length === 0) {
        showError(validation.error)
      } else {
        setError(null)
      }
      return
    }

    setSubmitting(true)
    setSubmitFailures([])
    setUploadPercent(null)
    const jobIds: string[] = []
    const failures: SubmitFailure[] = []

    try {
      let launchState = state
      const hasUniquifyTargets =
        state.videoFiles.length > 0 ||
        state.carouselBatches.some((b) => b.images.length > 0)
      if (state.makeAdsUnique && hasUniquifyTargets) {
        setUploadPercent(null)
        setProgressLabel("Uniquifying creatives…")
        try {
          const uniquifyResult = await uniquifyCreativesForLaunch(
            {
              videos: state.videoFiles,
              carouselBatches: state.carouselBatches,
            },
            setProgressLabel,
          )
          launchState = {
            ...state,
            videoFiles: uniquifyResult.videos,
            carouselBatches: uniquifyResult.carouselBatches,
          }
          if (uniquifyResult.skippedCount > 0) {
            setProgressLabel(
              `Uniquify ${uniquifyResult.uniquifiedCount} ok` +
                `, ${uniquifyResult.skippedCount} originals — uploading…`,
            )
          }
        } catch (err) {
          // Queueing uniquify jobs failed hard — fall back to originals and continue.
          const msg = err instanceof Error ? err.message : String(err)
          setProgressLabel(
            `Uniquify skipped (${msg}) — launching with original creatives…`,
          )
        }
      }

      setProgressLabel("Uploading creatives…")
      let fileManifest
      try {
        const uploadResult = await uploadCreativesSequentially(
          listCreativeParts(launchState),
          (progress) => {
            setProgressLabel(formatCreativeUploadLabel(progress))
            setUploadPercent(progress.percent)
          },
        )
        if (!uploadResult.success || !uploadResult.file_manifest) {
          throw new Error(
            uploadResult.detail ??
              uploadResult.message ??
              "Failed to upload creatives",
          )
        }
        fileManifest = uploadResult.file_manifest
      } catch (err) {
        if (err instanceof ApiError) {
          failures.push({
            campaignName: "(creatives upload)",
            message: err.message,
            status: err.status,
            requestId: err.requestId,
            bodySnippet: err.bodySnippet,
          })
        } else {
          const msg = err instanceof Error ? err.message : String(err)
          failures.push({
            campaignName: "(creatives upload)",
            message: msg,
            status: -1,
          })
        }
        setSubmitFailures(failures)
        showError("Failed to upload creatives — campaigns were not queued")
        scrollFormTop()
        return
      }

      for (let i = 0; i < state.campaigns.length; i++) {
        const entry = state.campaigns[i]
        setUploadPercent(null)
        setProgressLabel(
          `Queuing campaign ${i + 1}/${state.campaigns.length}…`,
        )
        const fd = buildFormData(launchState, entry, { fileManifest })
        try {
          const result = await submitMutation.mutateAsync(fd)
          if (!result.success) {
            throw new Error(
              result.detail ?? result.message ?? "Failed to queue",
            )
          }
          jobIds.push(result.job_id)
        } catch (err) {
          if (err instanceof ApiError) {
            failures.push({
              campaignName: entry.name,
              message: err.message,
              status: err.status,
              requestId: err.requestId,
              bodySnippet: err.bodySnippet,
            })
          } else {
            const msg = err instanceof Error ? err.message : String(err)
            failures.push({
              campaignName: entry.name,
              message: msg,
              status: -1,
            })
          }
        }
      }

      if (failures.length > 0) {
        setSubmitFailures(failures)
        showError(
          `${failures.length} of ${state.campaigns.length} campaign(s) failed to queue`,
        )
      }
      if (jobIds.length > 0) {
        setSuccess(
          `${jobIds.length} campaign(s) queued — processing in background`,
        )
        dispatch({ type: "RESET_CAMPAIGNS" })
        trackJobs(jobIds)
      }
      scrollFormTop()
    } finally {
      setSubmitting(false)
      setUploadPercent(null)
    }
  }

  return (
    /*
      One viewport-sized box. h-full plus an inner flex-1 used to stack a
      second full-height block under the launch row. h-0 + flex-1 takes
      only the free space in main; absolute inset-0 cannot grow past it.
    */
    <div className="relative h-0 min-h-0 w-full flex-1 overflow-hidden">
    <div className="create-campaign-theme-white absolute inset-0 mx-auto flex w-full max-w-[1600px] flex-col px-3 pt-3 text-white sm:px-4 sm:pt-4 lg:px-6 lg:pt-6">
      <div className="mb-3 flex shrink-0 justify-end">
        <Button
          type="button"
          variant="outline"
          className="h-9 border-[#4a4a4a] bg-[#262626] text-white hover:bg-[#303030]"
          onClick={() => setQueueOpen(true)}
        >
          Queue {jobs.length > 0 ? `(${jobs.length})` : ""}
        </Button>
      </div>

      <form
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
        onSubmit={handleSubmit}
      >
        <div data-form-scroll className="min-h-0 flex-1 space-y-5 overflow-y-auto pb-3">
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>
              <div>❌ {error}</div>
              {submitFailures.length > 0 ? (
                <div className="mt-3 space-y-2">
                  <ul className="space-y-2 rounded border border-destructive/40 bg-background/40 p-3 text-xs">
                    {submitFailures.map((f, idx) => (
                      <li key={`${f.campaignName}-${idx}`} className="break-words">
                        <div className="font-semibold">{f.campaignName}</div>
                        <div className="opacity-80">
                          status {f.status}
                          {f.requestId ? (
                            <>
                              {" · id "}
                              <code className="rounded bg-black/20 px-1">{f.requestId}</code>
                            </>
                          ) : null}
                        </div>
                        <div className="opacity-90">{f.message}</div>
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={copySupportReport}
                    >
                      {copyState === "copied"
                        ? "Copied ✓"
                        : copyState === "failed"
                          ? "Copy failed"
                          : "Copy error details for support"}
                    </Button>
                    <span className="text-xs opacity-70">
                      Paste into your support chat — includes request IDs.
                    </span>
                  </div>
                </div>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}

        {success ? (
          <Alert>
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        ) : null}

        <AccountStep
          state={state}
          dispatch={dispatch}
          campaignError={fieldErrors.campaigns}
        />

        {state.advertiserId && state.objectiveType ? (
          <AdGroupStep
            state={state}
            dispatch={dispatch}
            budgetError={fieldErrors.budget}
            locationsError={fieldErrors.locations}
            ageGroupsError={fieldErrors.ageGroups}
          />
        ) : null}

        {state.advertiserId && state.objectiveType ? (
          <AdsStep
            state={state}
            dispatch={dispatch}
            adTextError={fieldErrors.adText}
            smartAdTextsError={fieldErrors.smartAdTexts}
            ctaError={fieldErrors.ctas}
            campaignUrlError={fieldErrors.campaignUrls}
            creativesError={fieldErrors.creatives}
            carouselError={fieldErrors.carousel}
          />
        ) : null}
        </div>

        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-[#3f3f3f] bg-[#2a2a2a] py-3">
          <p className="text-xs text-white">
            {state.campaigns.length > 0
              ? `${state.campaigns.length} campaign${state.campaigns.length === 1 ? "" : "s"} ready to launch`
              : "Add at least one campaign to launch"}
          </p>
          <Button
            type="submit"
            disabled={submitting}
            className="h-10 rounded-md border border-[#8fa0ef]/35 bg-[#7488e8] px-6 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#687bda] disabled:cursor-not-allowed disabled:opacity-60"
          >
            🚀 Launch Campaign
          </Button>
        </div>
      </form>

      <SubmissionProgress
        active={submitting}
        label={progressLabel}
        percent={uploadPercent}
      />

      {queueOpen ? (
        <>
          <button
            type="button"
            aria-label="Close queue"
            className="fixed inset-0 z-40 bg-black/45"
            onClick={() => setQueueOpen(false)}
          />
          <aside className="fixed right-0 top-0 z-50 h-screen w-full max-w-[540px] border-l border-[#454545] bg-[#262626] p-4 shadow-[-10px_0_30px_rgba(0,0,0,0.45)]">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base font-semibold text-white">Campaign Queue</h2>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setQueueOpen(false)}
                className="h-8 text-white hover:bg-[#323232] hover:text-white"
              >
                Close
              </Button>
            </div>
            {panelVisible ? (
              <JobQueuePanel
                jobs={jobs}
                className="mb-0 h-[calc(100vh-84px)]"
                listClassName="max-h-none h-[calc(100vh-108px)]"
              />
            ) : (
              <div className="rounded-lg border border-[#4a4a4a] bg-[#2d2d2d] p-4 text-sm text-white">
                No campaigns in queue
              </div>
            )}
          </aside>
        </>
      ) : null}
    </div>
    </div>
  )
}
